import { vaultFetch, type AllowedCommands } from './vault-api-core.ts'

export type VaultItemType = 'variable' | 'password' | 'secret' | 'restricted'

export interface VaultValueSummary {
  id: string
  environment: string
  kekVersion: number | null
  kind: 'text' | 'file'
  filename: string
  expiresAt: string | null
  updatedAt: string
  [key: string]: unknown
}

export interface VaultItemRow {
  id: string
  key: string
  type: VaultItemType
  note: string
  allowedCommands: AllowedCommands
  pinnedBinary: string
  values: VaultValueSummary[]
  [key: string]: unknown
}

export interface VaultValuePutBody {
  ciphertext: string
  nonce: string
  wrappedDek: string | null
  kekVersion: number | null
  kind?: 'text' | 'file'
  filename?: string
  expiresAt?: string | null
}

// ── items and values ───────────────────────────────────────────────────
export async function listItems(
  rootId: string,
  nodeId: string,
): Promise<VaultItemRow[]> {
  const { items } = await vaultFetch<{ items: VaultItemRow[] }>(
    `/vault/admin/${rootId}/nodes/${nodeId}/items`,
  )
  return items
}

export function putItem(
  rootId: string,
  nodeId: string,
  key: string,
  body: {
    type: VaultItemType
    format?: string
    note?: string
    allowedCommands: AllowedCommands
    pinnedBinary?: string
  },
): Promise<VaultItemRow> {
  return vaultFetch<VaultItemRow>(
    `/vault/admin/${rootId}/nodes/${nodeId}/items/${encodeURIComponent(key)}`,
    { method: 'PUT', body: JSON.stringify(body) },
  )
}

export function deleteItem(
  rootId: string,
  nodeId: string,
  key: string,
): Promise<void> {
  return vaultFetch<undefined>(
    `/vault/admin/${rootId}/nodes/${nodeId}/items/${encodeURIComponent(key)}`,
    { method: 'DELETE' },
  )
}

export function putValue(
  rootId: string,
  nodeId: string,
  key: string,
  environment: string,
  body: VaultValuePutBody,
): Promise<{ ok: true }> {
  return vaultFetch(
    `/vault/admin/${rootId}/nodes/${nodeId}/items/${encodeURIComponent(key)}/values/${encodeURIComponent(environment)}`,
    { method: 'PUT', body: JSON.stringify(body) },
  )
}

export function deleteValue(
  rootId: string,
  nodeId: string,
  key: string,
  environment: string,
): Promise<void> {
  return vaultFetch<undefined>(
    `/vault/admin/${rootId}/nodes/${nodeId}/items/${encodeURIComponent(key)}/values/${encodeURIComponent(environment)}`,
    { method: 'DELETE' },
  )
}
